import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha } from '@mui/material/styles';
import Section from '../Section';
import Typography from '../Typography';
import Button from '../Button';
import Chip from '../Chip';
import Tooltip from '../Tooltip';
import { ArrowRightIcon, CheckIcon } from '../../components/Icons';
import { COLORS } from '../../themev2';

const HERO_MAX_WIDTH = 560;
const HERO_ASPECT_RATIO = '14 / 9';

const fadeUp = {
  '@keyframes emptyFadeUp': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

const enter = (delayMs: number) => ({
  animation: `emptyFadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export type ProductEmptyStep = {
  title: string;
  description: string;
  complete?: boolean;
};

export type ProductEmptyStateProps = {
  title: string;
  subtitle: string;
  chip?: string;
  benefits: string[];
  cta?: {
    label: string;
    onClick: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    isLocked?: boolean;
    tooltip?: string;
  };
  secondaryCta?: {
    label: string;
    onClick: () => void;
    startIcon?: React.ReactNode;
  };
  stepsTitle: string;
  steps: ProductEmptyStep[];
  discoverMoreLabel: string;
  onDiscoverMore: () => void;
  heroSrc?: string;
};

const ProductEmptyState = ({
  title,
  subtitle,
  chip,
  benefits,
  cta,
  secondaryCta,
  stepsTitle,
  steps,
  discoverMoreLabel,
  onDiscoverMore,
  heroSrc,
}: ProductEmptyStateProps) => {
  return (
    <Box
      sx={{
        ...fadeUp,
        display: 'flex',
        flexDirection: 'column',
        isolation: 'isolate',
        width: '100%',
        pb: { xs: 2, md: 4 },
      }}
    >
      <Section
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          mb: { xs: 0, md: '-48px' },
          padding: { xs: '24px', md: '32px' },
          borderRadius: '20px',
          overflow: 'hidden',
          background: COLORS.gray[50],
          border: `1px solid ${COLORS.gray[700]}`,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: { xs: '24px', md: '32px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '28px',
            flex: '1 1 auto',
            minWidth: 0,
            ...enter(40),
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: COLORS.gray[1300] }}>
                {title}
              </Typography>
              {chip && (
                <Chip
                  size="small"
                  label={chip}
                  sx={{
                    bgcolor: COLORS.orange[100],
                    color: COLORS.orange[900],
                    height: 28,
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: COLORS.gray[1100], maxWidth: '36em' }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {benefits.map((benefit, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: COLORS.secondary.cian,
                    '& svg': { width: 18, height: 18 },
                  }}
                >
                  <CheckIcon />
                </Box>
                <Typography variant="body1" sx={{ color: COLORS.gray[1100] }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
          {(cta || secondaryCta) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {cta && (
                <Tooltip title={cta.tooltip ?? ''} disableHoverListener={!cta.tooltip}>
                  {/* span needed to allow tooltip on disabled button */}
                  <Box component="span" sx={{ cursor: cta.isLocked ? 'not-allowed' : undefined }}>
                    <Button
                      onClick={cta.onClick}
                      isLoading={cta.isLoading}
                      disabled={cta.disabled || cta.isLocked}
                      endIcon={cta.isLocked ? <LockOutlinedIcon /> : <ArrowRightIcon />}
                      isFullHeight={false}
                    >
                      {cta.label}
                    </Button>
                  </Box>
                </Tooltip>
              )}
              {secondaryCta && (
                <Button
                  type="link"
                  color="tertiary"
                  startIcon={secondaryCta.startIcon}
                  isFullHeight={false}
                  onClick={secondaryCta.onClick}
                >
                  {secondaryCta.label}
                </Button>
              )}
            </Box>
          )}
        </Box>

        {heroSrc && (
          <Box
            sx={{
              position: 'relative',
              flex: { xs: '0 0 auto', md: `0 1 ${HERO_MAX_WIDTH}px` },
              width: '100%',
              maxWidth: { md: HERO_MAX_WIDTH },
              aspectRatio: HERO_ASPECT_RATIO,
              minWidth: 0,
              borderRadius: '16px',
              overflow: 'hidden',
              background: COLORS.gray[200],
              ...enter(120),
            }}
          >
            <Box
              component="img"
              src={heroSrc}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'left top',
              }}
            />
          </Box>
        )}
      </Section>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          pt: { xs: '32px', md: '80px' },
          pb: { xs: '32px', md: '40px' },
          px: { xs: '24px', md: '40px' },
          borderRadius: '20px',
          background: COLORS.gray[400],
          border: `1px solid ${COLORS.gray[700]}`,
          ...enter(220),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: COLORS.gray[1300] }}>
            {stepsTitle}
          </Typography>
          <Button
            type="text"
            color="secondary"
            size="small"
            endIcon={<ArrowRightIcon />}
            isFullHeight={false}
            onClick={onDiscoverMore}
          >
            {discoverMoreLabel}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            gap: { xs: '24px', md: '32px' },
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 0' },
                minWidth: { xs: '100%', md: 0 },
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '20px',
                    flexShrink: 0,
                    background: alpha(COLORS.indigo[600], 0.14),
                    color: COLORS.indigo[600],
                    '& svg': { width: 20, height: 20 },
                  }}
                >
                  {step.complete ? <CheckIcon /> : index + 1}
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    flex: 1,
                    height: '1px',
                    background: COLORS.indigo[100],
                    opacity: index < steps.length - 1 ? 1 : 0.55,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mt: '18px', fontWeight: 700, color: COLORS.gray[1300] }}>
                {step.title}
              </Typography>
              <Typography variant="body1" sx={{ mt: '6px', color: COLORS.gray[1100], maxWidth: 300 }}>
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductEmptyState;
